import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, map, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiError } from '../models/api-error.model';
import type { ApiResponse, PaginatedResult } from '../models/api-response.model';

export interface ApiRequestOptions {
  params?:
    | HttpParams
    | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
}

/**
 * Typed HTTP wrapper that unwraps the backend JSON envelope and surfaces errors as {@link ApiError}.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  get<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('GET', path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('POST', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('PUT', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('PATCH', path, body, options);
  }

  delete<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  /** POST a FormData payload (file upload). Browser sets the Content-Type + boundary automatically. */
  postForm<T>(path: string, formData: FormData): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(this.url(path), formData)
      .pipe(
        map((response) => this.unwrap(response)),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  /** GET list endpoint returning items plus pagination meta from the envelope. */
  list<T>(path: string, options?: ApiRequestOptions): Observable<PaginatedResult<T>> {
    return this.http
      .request<ApiResponse<T[]>>('GET', this.url(path), { params: options?.params })
      .pipe(
        map((response) => {
          if (!response.success) {
            throw new ApiError(response.message || 'Request failed.', response.errors);
          }

          const meta = response.meta as PaginatedResult<T>['meta'];

          return {
            items: response.data ?? [],
            meta: meta ?? null,
          };
        }),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  private request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: ApiRequestOptions,
  ): Observable<T> {
    return this.http
      .request<ApiResponse<T>>(method, this.url(path), {
        body,
        params: options?.params,
      })
      .pipe(
        map((response) => this.unwrap(response)),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  private unwrap<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new ApiError(response.message || 'Request failed.', response.errors);
    }

    return response.data as T;
  }

  private handleError(error: unknown): Observable<never> {
    if (error instanceof ApiError) {
      return throwError(() => error);
    }

    if (error instanceof HttpErrorResponse) {
      const body = error.error as ApiResponse | null;

      if (body && typeof body === 'object' && 'success' in body && body.success === false) {
        return throwError(
          () => new ApiError(body.message || error.statusText, body.errors, error.status),
        );
      }

      if (error.status === 0) {
        return throwError(
          () =>
            new ApiError(
              'Cannot reach the API server. In a separate terminal run: cd backend && ./serve.sh',
              null,
              error.status,
            ),
        );
      }

      if (error.status === 500) {
        return throwError(
          () => new ApiError('An unexpected server error occurred. Please try again.', null, 500),
        );
      }

      return throwError(() => new ApiError(error.message, null, error.status));
    }

    return throwError(() => error);
  }

  private url(path: string): string {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const cleanPath = path.replace(/^\//, '');

    return `${base}/${cleanPath}`;
  }
}
